/**
 * Admin approvals queue — list pending trainer signups, approve or reject.
 */
import { useEffect, useState } from "react";
import { Mast } from "../components/Mast.js";
import { api } from "../lib/api.js";

interface Approval {
  id: number;
  user_id: string;
  type: string;
  state: string;
  email: string;
  display_name?: string;
  created_at: string;
}

export default function AdminApprovals() {
  const [items, setItems]  = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]    = useState<number | null>(null);
  const [note, setNote]    = useState("");
  const [editing, setEditing] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    const r = await api<{ ok: true; approvals: Approval[] }>("/api/admin/approvals");
    setItems(r.approvals);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function resolve(id: number, action: "approve" | "reject") {
    setBusy(id);
    try {
      await api(`/api/admin/approvals/${id}`, {
        method: "POST",
        body: { action, note: note || undefined },
      });
      setNote("");
      setEditing(null);
      await load();
    } catch (err: any) {
      alert(`Action failed: ${err?.body?.error ?? err?.message}`);
    } finally { setBusy(null); }
  }

  return (
    <>
      <Mast section="Admin · approvals" />
      <main className="page">
        <section style={{ marginBottom: "var(--s-7)" }}>
          <div className="serial rise rise-1" style={{ marginBottom: "var(--s-3)" }}>
            QUEUE · {items.length} 筆待審
          </div>
          <h1 className="rise rise-2" style={{ maxWidth: 760 }}>
            訓練師<span className="italic-display" style={{ color: "var(--rust)" }}>申請審核</span>
          </h1>
          <p className="rise rise-3 muted" style={{ marginTop: "var(--s-3)", fontSize: "var(--t-md)", maxWidth: 600 }}>
            通過後 user 角色會從 <code>trainer_pending</code> 升 <code>trainer</code>，立即可登入。
          </p>
        </section>

        <section className="rise rise-4">
          {loading ? (
            <div className="muted">載入中…</div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="italic-display" style={{ fontSize: "var(--t-xl)", marginBottom: "var(--s-2)" }}>
                沒有待審項目
              </div>
              <div className="muted">所有申請都已處理</div>
            </div>
          ) : (
            <div className="col" style={{ gap: "var(--s-3)" }}>
              {items.map((a) => (
                <article key={a.id} className="surface" style={{ padding: "var(--s-5)" }}>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "var(--s-4)",
                    alignItems: "start",
                  }}>
                    <div>
                      <div className="serial" style={{ marginBottom: "var(--s-2)" }}>
                        申請 #{a.id} · {a.created_at}
                      </div>
                      <h3 style={{ fontSize: "var(--t-xl)", marginBottom: "var(--s-2)" }}>
                        <span className="italic-display">{a.display_name ?? "—"}</span>
                      </h3>
                      <div className="kv tabular" style={{ marginTop: "var(--s-3)" }}>
                        <span className="kv__key">Email</span>
                        <span className="kv__val">{a.email}</span>
                        <span className="kv__key">User ID</span>
                        <span className="kv__val" style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)" }}>{a.user_id}</span>
                        <span className="kv__key">Type</span>
                        <span className="kv__val">{a.type}</span>
                      </div>
                    </div>
                    <span className="chip chip--warning">pending</span>
                  </div>

                  {editing === a.id && (
                    <div style={{ marginTop: "var(--s-4)" }}>
                      <div className="field">
                        <label className="field-label">備註 (選填，記錄拒絕原因或審核理由)</label>
                        <textarea
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="例：經查屬實，是 OT 訓練師，准予開通。"
                        />
                      </div>
                    </div>
                  )}

                  <div className="row row--end" style={{ marginTop: "var(--s-4)", gap: "var(--s-3)" }}>
                    {editing !== a.id ? (
                      <>
                        <button className="btn btn--ghost btn--small" onClick={() => setEditing(a.id)}>
                          ＋ 加備註
                        </button>
                        <button
                          className="btn btn--small"
                          style={{ background: "var(--paper-warm)", color: "var(--danger)", borderColor: "var(--danger)" }}
                          onClick={() => resolve(a.id, "reject")}
                          disabled={busy === a.id}
                        >
                          {busy === a.id ? "…" : "✗ 拒絕"}
                        </button>
                        <button
                          className="btn btn--rust btn--small"
                          onClick={() => resolve(a.id, "approve")}
                          disabled={busy === a.id}
                        >
                          {busy === a.id ? "…" : "✓ 通過"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn--ghost btn--small" onClick={() => { setEditing(null); setNote(""); }}>
                          取消
                        </button>
                        <button
                          className="btn btn--small"
                          style={{ background: "var(--paper-warm)", color: "var(--danger)", borderColor: "var(--danger)" }}
                          onClick={() => resolve(a.id, "reject")}
                          disabled={busy === a.id}
                        >
                          ✗ 拒絕（含備註）
                        </button>
                        <button
                          className="btn btn--rust btn--small"
                          onClick={() => resolve(a.id, "approve")}
                          disabled={busy === a.id}
                        >
                          ✓ 通過（含備註）
                        </button>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}
